import { useCallback, useEffect, useMemo, useState } from 'react';
import { ListFilters, type SortOption } from '../components/ListFilters';
import { ViewHeader } from '../components/ViewHeader';
import { usePersistedState } from '../hooks/usePersistedState';
import { useMe } from '../hooks/useMe';
import { useUserTeams } from '../hooks/useUserTeams';
import { useAuth } from '../context/AuthContext';
import { filterByViewer } from '../utils/viewerFilter';
import { searchTasks } from '../utils/searchTasks';
import type { Task } from '../types';
import * as api from '../utils/apiClient';

function formatDeletedAt(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

type TrashSort = 'recent' | 'oldest' | 'alpha';
const SORT_OPTIONS: SortOption[] = [
  { value: 'recent', label: 'Recent first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'alpha', label: 'Alphabetical' },
];

function sortTrash(tasks: Task[], mode: TrashSort): Task[] {
  const copy = [...tasks];
  switch (mode) {
    case 'recent':
      return copy.sort((a, b) => (b.deletedAt ?? '').localeCompare(a.deletedAt ?? ''));
    case 'oldest':
      return copy.sort((a, b) => (a.deletedAt ?? '').localeCompare(b.deletedAt ?? ''));
    case 'alpha':
      return copy.sort((a, b) => a.title.localeCompare(b.title));
  }
}

export function TrashView() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showingAll, setShowingAll] = useState(false);
  const [viewer, setViewer] = usePersistedState<string>('viewer.selected', 'self');
  const [sortMode, setSortMode] = usePersistedState<TrashSort>('sort.trash', 'recent');
  const { me } = useMe();
  const { userTeams } = useUserTeams();
  const { user } = useAuth();

  const refresh = useCallback(async (includeAll: boolean) => {
    setLoading(true);
    try {
      setTasks(await api.listTasks('deleted', { all: includeAll }));
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh(showingAll);
  }, [refresh, showingAll]);

  const viewerFiltered = useMemo(
    () => filterByViewer(tasks, viewer, { mySub: me?.sub ?? user?.userId ?? null, userTeams }),
    [tasks, viewer, me, user, userTeams]
  );
  const searched = useMemo(() => searchTasks(viewerFiltered, query), [viewerFiltered, query]);
  const sorted = useMemo(() => sortTrash(searched, sortMode), [searched, sortMode]);

  const onRestore = useCallback(async (task: Task) => {
    setBusyId(task.taskId);
    try {
      await api.restoreTask(task.teamId, task.taskId);
      setTasks((prev) => prev.filter((t) => t.taskId !== task.taskId));
    } finally {
      setBusyId(null);
    }
  }, []);

  const onPurge = useCallback(async (task: Task) => {
    if (!window.confirm(`Permanently delete "${task.title}"? This cannot be undone.`)) return;
    setBusyId(task.taskId);
    try {
      await api.purgeTask(task.teamId, task.taskId);
      setTasks((prev) => prev.filter((t) => t.taskId !== task.taskId));
    } finally {
      setBusyId(null);
    }
  }, []);

  return (
    <div className="scope-view">
      <ViewHeader title="Trash" searchValue={query} onSearchChange={setQuery} />
      <div className="pane-wrap">
        <section className="list-pane" aria-label="Deleted tasks">
          {loading ? (
            <div className="muted">Loading…</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : (
            <>
              <ListFilters
                sortMode={sortMode}
                onSortChange={(m) => setSortMode(m as TrashSort)}
                sortOptions={SORT_OPTIONS}
                viewer={viewer}
                onViewerChange={setViewer}
              />
              {sorted.length === 0 ? (
                <div className="task-list-empty">Trash is empty.</div>
              ) : (
                <div className="task-list" role="list">
                  {sorted.map((task) => {
                    const groupColor =
                      me?.groups.find((g) => g.id === task.teamId)?.color ?? 'transparent';
                    const busy = busyId === task.taskId;
                    return (
                      <div
                        key={task.taskId}
                        className="task-row is-deleted"
                        style={{
                          ['--row-accent' as string]: groupColor,
                          borderLeft: `3px solid ${groupColor}`,
                        }}
                        role="listitem"
                      >
                        <div />
                        <div className="task-row-body">
                          <div className="task-row-title-line">
                            <div className="task-row-title">
                              <span>{task.title}</span>
                            </div>
                            <span className="task-row-due">deleted {formatDeletedAt(task.deletedAt)}</span>
                          </div>
                          <div className="trash-actions">
                            <button disabled={busy} onClick={() => void onRestore(task)}>
                              Restore
                            </button>
                            <button className="danger" disabled={busy} onClick={() => void onPurge(task)}>
                              Delete forever
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {!showingAll && !loading && (
                <div className="load-older">
                  <button type="button" onClick={() => setShowingAll(true)}>
                    Load older
                  </button>
                  <span className="muted"> · showing last 30 days</span>
                </div>
              )}
              {showingAll && (
                <div className="load-older muted">Showing all</div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
